import React from 'react';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';
import Logo from '../assets/logo';
import AppFooter from '../components/common/AppFooter';
import LanguageSwitcher from '../components/common/LanguageSwitcher';

/**
 * AuthLayout — CHANGED in 1.17.0-dev.1 (Vikar Experten design system).
 *
 *   - The header is the brand's RED masthead. The wordmark is white, so red (or
 *     ink) is the only surface it may sit on. The separate blue "app name" text
 *     beside the old placeholder icon is removed — the logo already says it.
 *   - The auth box takes the approved 6px corners, a 1px hairline and the
 *     faintest app shadow, instead of an 8px radius on a two-layer drop shadow.
 *   - The masthead takes the 3px chrome radius, matching the admin rail.
 *   - Values come from the theme; nothing is hard-coded any more (the old file
 *     hard-coded #F7F9FA, #FFFFFF and #0055A4 to "reduce theme dependencies").
 *   - useTranslation is no longer needed here, since the only string was the
 *     app name that the logo now carries.
 */

const AuthContainer = styled.div`
  display:flex;
  flex-direction:column;
  min-height:100vh;
  background-color:${({ theme }) => theme.colors.offWhite};
`;

const AuthHeader = styled.header`
  padding:${({ theme }) => theme.spacing[5]} 0;
  background-color:${({ theme }) => theme.colors.brandRed};
  border-radius:${({ theme }) => theme.components.chrome.borderRadius};
  margin:${({ theme }) => theme.components.chrome.inset};
  position:relative;
  z-index:10;
`;

const HeaderContent = styled.div`
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:${({ theme }) => theme.spacing[5]};
  max-width:${({ theme }) => theme.grid.containerWidth.xxl};
  margin:0 auto;
  padding:0 ${({ theme }) => theme.spacing[8]};

  @media (max-width:576px){padding:0 ${({ theme }) => theme.spacing[4]}}
`;

const LogoWrapper = styled.div`
  height:38px;
  display:flex;
  align-items:center;

  svg,img{height:100%;width:auto;display:block}
`;

/* The switcher sits on the red plate, so it inverts. */
const HeaderActions = styled.div`
  display:flex;
  align-items:center;

  select,button{
    background-color:transparent;
    color:${({ theme }) => theme.colors.white};
    border-color:rgba(255,255,255,.35);
  }
  select option{color:${({ theme }) => theme.colors.text}}
`;

const ContentWrapper = styled.main`
  flex:1;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  padding:${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[4]};

  @media (min-width:768px){
    padding:${({ theme }) => theme.spacing[16]} ${({ theme }) => theme.spacing[4]};
  }
`;

const AuthBox = styled.div`
  width:100%;
  max-width:440px;
  background-color:${({ theme }) => theme.colors.white};
  border:${({ theme }) => theme.components.card.border};
  border-radius:${({ theme }) => theme.components.card.borderRadius};
  box-shadow:${({ theme }) => theme.shadows.xs};
  padding:${({ theme }) => theme.spacing[8]};

  @media (min-width:768px){padding:${({ theme }) => theme.spacing[10]}}
`;

const AuthLayout = () => (
  <AuthContainer>
    <AuthHeader>
      <HeaderContent>
        <LogoWrapper>
          <Logo height={38} />
        </LogoWrapper>
        <HeaderActions>
          <LanguageSwitcher />
        </HeaderActions>
      </HeaderContent>
    </AuthHeader>

    <ContentWrapper>
      <AuthBox data-testid="auth-box">
        <Outlet />
      </AuthBox>
    </ContentWrapper>

    <AppFooter />
  </AuthContainer>
);

export default AuthLayout;
